import api from '../../../services/api'
import i18n from '../../../i18n'
import { dateLocale } from '../../../i18n/format'
import type { BruktbilFilters } from '../useBruktbilFilters'

// Ståtid has its own filter set (backend #274): the shared klasse/varebil/
// omregistrering filters do NOT apply. Period (slutt_dato window) and
// selger-postnummer (geo basis = last dealer) carry over from the layout;
// the module-local filters below come from StaatidFilterBar.
export type StaatidParams = Pick<BruktbilFilters, 'from' | 'to'> & {
  selgerPostnummer?: string | null
  antallTransfers?: number[] // 2, 3, 4 exact; 5 means >= 5
  startClass?: string[] // how the car entered bransjen
  sluttkundeType?: string[] // PERSON / FIRMA / BILUTLEIE / LEASING
  drivstoffGruppe?: string[] // drivlinje, hybrids split out of Bensin/Diesel (#800)
  merke?: string[] // brand filter (Spørring / forhandler-detaljer)
  modell?: string[]
  dealerOrgNr?: string[] // selling bilbransje org
  aarsmodellMin?: number // inclusive first-registration-year bounds (#364)
  aarsmodellMax?: number
}

// Labels are translation keys, resolved at render (#714).
export const START_CLASS_OPTIONS = [
  { value: 'PERSON_TO_BILBRANSJE', labelKey: 'klasse.PERSON_TO_BILBRANSJE' },
  { value: 'LEASING_TO_BILBRANSJE', labelKey: 'klasse.LEASING_TO_BILBRANSJE' },
  { value: 'FIRMA_TO_BILBRANSJE', labelKey: 'klasse.FIRMA_TO_BILBRANSJE' },
  { value: 'BILUTLEIE_TO_BILBRANSJE', labelKey: 'klasse.BILUTLEIE_TO_BILBRANSJE' },
  { value: 'FORSIKRING_TO_BILBRANSJE', labelKey: 'klasse.FORSIKRING_TO_BILBRANSJE' },
] as const

export const SLUTTKUNDE_OPTIONS = [
  { value: 'PERSON', labelKey: 'sluttkundeType.PERSON' },
  { value: 'FIRMA', labelKey: 'sluttkundeType.FIRMA' },
  { value: 'BILUTLEIE', labelKey: 'sluttkundeType.BILUTLEIE' },
  { value: 'LEASING', labelKey: 'sluttkundeType.LEASING' },
] as const

// The five drivlinjer (#800), in display order. value is the API's
// drivstoff_gruppe; the label is the issue's own wording, verbatim.
export const DRIVLINJE_OPTIONS = [
  { value: 'Elektrisk', labelKey: 'drivlinje.Elektrisk' },
  { value: 'Bensin', labelKey: 'drivlinje.Bensin' },
  { value: 'Diesel', labelKey: 'drivlinje.Diesel' },
  { value: 'Hybrid', labelKey: 'drivlinje.Hybrid' },
  { value: 'Ladbar hybrid', labelKey: 'drivlinje.LadbarHybrid' },
] as const

export interface StaatidBucket {
  label: string
  antall: number
}

export interface AntallTransfersFordeling {
  antall_transfers: number // 2, 3, 4 exact; 5 aggregates >= 5
  antall: number
  median_dager: number | null
}

export interface StaatidTrendPoint {
  maned: string // "YYYY-MM"
  antall: number
  median_dager: number | null
  /** Mean for the same month (#490) — drawn as a second line next to the
   *  median so a long tail of slow cars is visible rather than averaged away. */
  snitt_dager?: number | null
}

export interface StaatidOversiktResponse {
  totalt_antall_salg: number
  antall_parede: number
  dekningsgrad: number
  median_dager: number | null
  snitt_dager: number | null
  buckets: StaatidBucket[]
  antall_transfers_fordeling: AntallTransfersFordeling[]
  trend: StaatidTrendPoint[]
}

export interface StaatidForhandlerRow {
  dealer_org_nr: string
  dealer_org_navn: string | null
  antall_roundtrips: number
  median_dager: number | null
  snitt_dager: number | null
  median_siste_ledd_dager: number | null
  /** Kommisjonssalg (#661): paired roundtrips with 0 days' ståtid. Flagged,
   *  not excluded — they are still in the median, the mean and the ranking. */
  antall_kommisjon: number
  /** antall_kommisjon / antall_roundtrips, 0-1. */
  andel_kommisjon: number
}

export interface StaatidForhandlereResponse {
  rows: StaatidForhandlerRow[]
  total: number
}

export interface StaatidMerkeRow {
  merke: string | null
  modell: string | null
  antall: number
  median_dager: number | null
  snitt_dager: number | null
  trend: StaatidTrendPoint[]
  /** Frontend-only: a pair's own merke, set only by SporringTab's dealer x
   *  merke pair normalization (#471) — not part of the /staatid/merker
   *  response this type otherwise mirrors. */
  pairMerke?: string | null
}

export interface StaatidMerkerResponse {
  rows: StaatidMerkeRow[]
  total: number
  drilldown: boolean
}

export interface StaatidRoundtripRow {
  slutt_svv_id: number
  slutt_dato: string
  staatid_dager: number
  merke: string | null
  modell: string | null
  /** Null for basic callers — the API strips the dealer fields (#673). */
  dealer_org_nr: string | null
  dealer_org_navn: string | null
  antall_transfers: number
}

export interface StaatidRoundtripsResponse {
  rows: StaatidRoundtripRow[]
  total: number
}

export type ForhandlerSort = 'antall_roundtrips' | 'median_dager'

// ── Dealer comparison (multi-select dealers as separate trend series) ──────

export interface StaatidForhandlerTrendRow {
  dealer_org_nr: string
  dealer_org_navn: string | null
  /** Populated once merke[] is non-empty (#471) — one row per requested
   *  (dealer_org_nr, merke) pair instead of one row per dealer. */
  merke: string | null
  antall: number
  median_dager: number | null
  snitt_dager: number | null
  trend: StaatidTrendPoint[]
}

export interface StaatidForhandlereTrendResponse {
  rows: StaatidForhandlerTrendRow[]
  total: number
}

// ── Fase 3 — roundtrip trail drilldown ───────────────────────────────────

export interface StaatidTrailLeg {
  svv_id: number
  hendelse_tidspunkt: string
  class: string | null
  seller_org_nr: string | null
  seller_org_navn: string | null
  buyer_org_nr: string | null
  buyer_org_navn: string | null
  seller_postnummer: string | null
  buyer_postnummer: string | null
  dager_til_neste: number | null // null on the last leg
}

export interface StaatidTrailResponse {
  slutt_svv_id: number
  kuid: number
  dealer_org_nr: string
  dealer_org_navn: string | null
  merke: string | null
  modell: string | null
  staatid_dager: number | null
  siste_ledd_dager: number | null
  antall_transfers: number | null
  paired: boolean
  legs: StaatidTrailLeg[]
}

// ── Innsikt (#662) ──────────────────────────────────────────────────────────

/** One kanal x ledd cell, the dealer against the market. Kommisjonssalg are
 *  outside every figure here. */
export interface StaatidInnsiktSegment {
  kanal: 'innbytte' | 'leasing' | 'annet'
  ledd: 'direkte' | 'via'
  antall: number
  median_dager: number | null
  marked_antall: number
  marked_median_dager: number | null
  marked_p90_dager: number | null
  diff_dager: number | null
  diff_andel: number | null
  /** Too few sales to trust the median: shown as «for få salg», never flagged. */
  for_faa_salg: boolean
  flagget: boolean
}

export interface StaatidInnsiktUteligger {
  slutt_svv_id: number
  slutt_dato: string
  merke: string | null
  modell: string | null
  bil_alder_ar: number | null
  drivstoff: string | null
  kanal: StaatidInnsiktSegment['kanal']
  ledd: StaatidInnsiktSegment['ledd']
  staatid_dager: number
  marked_p90_dager: number
  dager_over_p90: number
}

export interface StaatidInnsiktResponse {
  dealer_org_nr: string
  dealer_org_navn: string | null
  antall_roundtrips: number
  antall_kommisjon: number
  andel_kommisjon: number
  /** Always six, in display order. */
  segmenter: StaatidInnsiktSegment[]
  storst_potensial: StaatidInnsiktSegment | null
  /** Capped at 50, most days over P90 first; antall_uteliggere is the full count. */
  uteliggere: StaatidInnsiktUteligger[]
  antall_uteliggere: number
}

/** filters must carry exactly one dealerOrgNr. */
export function fetchStaatidInnsikt(filters: StaatidParams): Promise<{ data: StaatidInnsiktResponse }> {
  return api.get(`/bruktbil/staatid/innsikt?${buildQS(filters, {})}`)
}

export function fetchStaatidTrail(sluttSvvId: number): Promise<{ data: StaatidTrailResponse }> {
  return api.get(`/bruktbil/staatid/roundtrips/${sluttSvvId}/trail`)
}

/** Stored in error state for a non-Error rejection and translated at render,
 *  so the message follows a language switch (#714). */
export const UNKNOWN_ERROR = '__staatid_unknown_error__'

/** A 0-1 share as "25,0 %" — always one decimal, so 0 reads "0,0 %", not "—". */
export function formatAndel(v: number): string {
  return `${(v * 100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}

/** nb-NO day count, e.g. "33" / "33,5"; em-dash for null. */
export function formatDager(v: number | null): string {
  if (v === null) return '—'
  return v.toLocaleString('nb-NO', { maximumFractionDigits: 1 })
}

/** Dealer vs. market median delta, e.g. "33 % raskere enn markedet"
 * (fewer days ståtid = faster turnover = better). null when either side
 * is missing (no data yet, or a zero-length market slice). */
export function formatBenchmarkDelta(
  dealerMedian: number | null, marketMedian: number | null,
): string | null {
  if (dealerMedian === null || marketMedian === null || marketMedian === 0) return null
  const diffPct = ((marketMedian - dealerMedian) / marketMedian) * 100
  const rounded = Math.round(Math.abs(diffPct))
  // Called during render, so the language in force is the one shown (#714).
  if (rounded === 0) return i18n.t('detalj.likt', { ns: 'staatid' })
  return diffPct > 0
    ? i18n.t('detalj.raskere', { ns: 'staatid', n: rounded })
    : i18n.t('detalj.tregere', { ns: 'staatid', n: rounded })
}

/** "YYYY-MM" -> "september 2026" / "September 2026" in the UI language —
 *  the partial-month caveat under the trend charts (#714). In Norwegian this
 *  is exactly partialPeriod's monthNameNo. */
export function formatMonthName(maned: string): string {
  const [y, m] = maned.split('-').map(Number)
  const name = new Intl.DateTimeFormat(dateLocale(), { month: 'long', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, 1)))
  return `${name} ${y}`
}

/** Dekningsgrad 0..1 -> "93,3 %". */
export function formatDekningsgrad(v: number): string {
  return `${(v * 100).toLocaleString('nb-NO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`
}

// Build URLs with URLSearchParams so the literal `merke[]` key reaches
// FastAPI — axios' paramsSerializer strips the brackets (see MerkeModell/api.ts
// and Priser/api.ts, which hit the same bug).
function buildQS(
  filters: StaatidParams,
  extras: Record<string, string | number | string[] | undefined>,
): string {
  const qs = new URLSearchParams()
  qs.set('from_date', filters.from)
  qs.set('to_date', filters.to)
  if (filters.selgerPostnummer) {
    for (const p of filters.selgerPostnummer.split(',')) qs.append('selger_postnummer', p.trim())
  }
  // Module-local filters — plain repeated keys (no [] alias on the backend)…
  for (const a of filters.antallTransfers ?? []) qs.append('antall_transfers', String(a))
  for (const s of filters.startClass ?? []) qs.append('start_class', s)
  for (const s of filters.sluttkundeType ?? []) qs.append('sluttkunde_type', s)
  for (const g of filters.drivstoffGruppe ?? []) qs.append('drivstoff_gruppe', g)
  for (const d of filters.dealerOrgNr ?? []) qs.append('dealer_org_nr', d)
  // …except merke/modell, which the backend declares with a [] alias.
  for (const m of filters.merke ?? []) qs.append('merke[]', m)
  for (const m of filters.modell ?? []) qs.append('modell[]', m)
  if (filters.aarsmodellMin !== undefined) qs.set('aarsmodell_min', String(filters.aarsmodellMin))
  if (filters.aarsmodellMax !== undefined) qs.set('aarsmodell_max', String(filters.aarsmodellMax))
  for (const [key, val] of Object.entries(extras)) {
    if (val === undefined) continue
    if (Array.isArray(val)) {
      for (const v of val) qs.append(key, v)
    } else {
      qs.set(key, String(val))
    }
  }
  return qs.toString()
}

export function fetchStaatidOversikt(
  filters: StaatidParams,
): Promise<{ data: StaatidOversiktResponse }> {
  return api.get(`/bruktbil/staatid/oversikt?${buildQS(filters, {})}`)
}

export function fetchStaatidForhandlere(
  filters: StaatidParams,
  sort: ForhandlerSort,
  sortDir: 'asc' | 'desc',
  limit = 50,
  offset = 0,
  q?: string, // dealer name contains / org_nr prefix (#364)
  minAntall?: number, // drop dealers below this roundtrip count
): Promise<{ data: StaatidForhandlereResponse }> {
  const qs = buildQS(filters, {
    sort, sort_dir: sortDir, limit, offset,
    q: q || undefined, min_antall: minAntall,
  })
  return api.get(`/bruktbil/staatid/forhandlere?${qs}`)
}

export type StaatidGruppering = 'merke' | 'modell'

export function fetchStaatidMerker(
  filters: StaatidParams,
  merkeFilter: string[],
  limit = 50,
  offset = 0,
  // #695: 'modell' lists every model across merker (Ståtid → Modeller); q is
  // a server-side search on merke, or merke or modell for models.
  opts: { gruppering?: StaatidGruppering; q?: string } = {},
): Promise<{ data: StaatidMerkerResponse }> {
  const qs = buildQS(filters, {
    'merke[]': merkeFilter.length ? merkeFilter : undefined,
    limit,
    offset,
    gruppering: opts.gruppering === 'modell' ? 'modell' : undefined,
    q: opts.q?.trim() || undefined,
  })
  return api.get(`/bruktbil/staatid/merker?${qs}`)
}

/** Per-dealer monthly medians for the dealer-comparison scatter series —
 * one row per dealerOrgNr, including a zero-antall row for a dealer with no
 * matching data in-period. Requires >= 1 dealer (422 otherwise). */
export function fetchStaatidForhandlereTrend(
  filters: StaatidParams,
  dealerOrgNr: string[],
): Promise<{ data: StaatidForhandlereTrendResponse }> {
  const qs = buildQS({ ...filters, dealerOrgNr }, {})
  return api.get(`/bruktbil/staatid/forhandlere/trend?${qs}`)
}

export function fetchStaatidRoundtrips(
  filters: StaatidParams,
  limit = 1000,
  offset = 0,
  sample?: number,
): Promise<{ data: StaatidRoundtripsResponse }> {
  const qs = buildQS(filters, { limit, offset, sample })
  return api.get(`/bruktbil/staatid/roundtrips?${qs}`)
}

// Scatter sampling (#456): a single request with `sample` set to the
// budget. The backend thins a large matching set to an even spread via a
// deterministic stride on the primary key — replaces the earlier frontend
// month-bucketing (#455), which still clustered into narrow spikes at high
// volume since each bucket only fetched the first few days of its month
// (offset=0 against a chronologically-sorted endpoint). `sample` is a no-op
// when the filtered set is already <= the budget, so this is always safe
// to send.
const SCATTER_BUDGET = 2000

export async function loadScatterSample(filters: StaatidParams): Promise<StaatidRoundtripsResponse> {
  const r = await fetchStaatidRoundtrips(filters, SCATTER_BUDGET, 0, SCATTER_BUDGET)
  return r.data
}

/** All points for one month (max 2000) — the month-zoom path, no sampling. */
export function loadScatterMonth(
  filters: StaatidParams, month: string,
): Promise<{ data: StaatidRoundtripsResponse }> {
  return fetchStaatidRoundtrips({ ...filters, ...monthRange(month) }, 2000, 0)
}

/** "YYYY-MM" -> {from, to} covering that whole month. */
export function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number)
  const last = new Date(y, m, 0).getDate() // day 0 of next month = last day
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

// ── Drivlinjer (#800) ──────────────────────────────────────────────────────

export interface StaatidDrivlinjeRow {
  drivstoff_gruppe: string
  antall: number
  /** antall / total over the rows returned, 0-1. */
  andel: number
  median_dager: number | null
  snitt_dager: number | null
  /** Months with fewer than 3 roundtrips are left out — draw a gap. */
  trend: StaatidTrendPoint[]
}

export interface StaatidDrivlinjerResponse {
  /** Fixed order; zero-antall rows included. Only the selected drivlinjer
   *  when drivstoffGruppe is set. */
  rows: StaatidDrivlinjeRow[]
  total: number
}

export function fetchStaatidDrivlinjer(
  filters: StaatidParams,
): Promise<{ data: StaatidDrivlinjerResponse }> {
  return api.get(`/bruktbil/staatid/drivlinjer?${buildQS(filters, {})}`)
}

// ── Bilalder-korrelasjon og merke/modell-detalj (#490) ────────────────────

export interface StaatidAlderBucket {
  label: string
  alder_min: number
  alder_max: number | null
  antall: number
  median_dager: number | null
  snitt_dager: number | null
}

export interface StaatidAlderFordelingResponse {
  rows: StaatidAlderBucket[]
  total: number
  uten_alder: number
}

export interface StaatidMerkeDetaljResponse {
  merke: string
  modell: string | null
  antall: number
  median_dager: number | null
  snitt_dager: number | null
  trend: StaatidTrendPoint[]
  alder_fordeling: StaatidAlderBucket[]
}

/** Ståtid per bilalder-interval for whatever the filters select (merke,
 *  modell, dealer, or the whole market). */
export function fetchStaatidAlderFordeling(
  filters: StaatidParams,
): Promise<{ data: StaatidAlderFordelingResponse }> {
  return api.get(`/bruktbil/staatid/alder-fordeling?${buildQS(filters, {})}`)
}

/** One merke's (or modell's) detail page data. Rejects with a 404 when the
 *  merke has no paired roundtrips in the selected period. */
export function fetchStaatidMerkeDetalj(
  filters: StaatidParams,
  merke: string,
  modell?: string | null,
): Promise<{ data: StaatidMerkeDetaljResponse }> {
  const qs = buildQS(filters, { modell: modell || undefined })
  return api.get(`/bruktbil/staatid/merker/${encodeURIComponent(merke)}?${qs}`)
}
